import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Activity,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Coins,
  Copy,
  Database,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import {
  AdminApiError,
  addEntitlementQuota,
  createEntitlement,
  deleteEntitlement,
  listEntitlements,
  validateAdminKey,
} from "../lib/adminApi";
import "./AdminConsole.css";

const SESSION_KEY = "recapmate.admin.apiKey";
const number = new Intl.NumberFormat("en-GB");
const emptyPage = {
  items: [],
  page: 1,
  pageSize: 20,
  totalItems: 0,
  totalPages: 0,
  stats: {
    totalKeys: 0,
    activeKeys: 0,
    depletedKeys: 0,
    totalQuota: 0,
    remainingQuota: 0,
    consumedQuota: 0,
  },
};

function maskKey(value) {
  if (!value) return "";
  if (value.length <= 16) return `${value.slice(0, 4)}••••${value.slice(-4)}`;
  return `${value.slice(0, 8)}••••••••${value.slice(-6)}`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function quotaPercent(item) {
  if (!item.totalQuota) return 0;
  return Math.max(0, Math.min(100, (item.remainingQuota / item.totalQuota) * 100));
}

function copyText(value, onCopied) {
  navigator.clipboard.writeText(value).then(onCopied).catch(() => {});
}

function AdminDialog({ open, onOpenChange, title, description, children }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-slate-950/45 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[120] max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl sm:p-7">
          <div className="pr-10">
            <Dialog.Title className="admin-display text-2xl font-semibold text-slate-950">
              {title}
            </Dialog.Title>
            {description && (
              <Dialog.Description className="mt-2 text-sm leading-6 text-slate-600">
                {description}
              </Dialog.Description>
            )}
          </div>
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-5 top-5 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Close dialog"
            >
              <X className="h-5 w-5" />
            </button>
          </Dialog.Close>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AdminSignIn({ onAuthenticated, initialError }) {
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError || "");

  const submit = async (event) => {
    event.preventDefault();
    const candidate = key.trim();
    if (!candidate) {
      setError("Enter an access key.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await validateAdminKey(candidate);
      onAuthenticated(candidate);
    } catch {
      setError("Access denied.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="admin-shell flex min-h-screen items-center justify-center px-5 py-10">
      <section className="w-full max-w-sm">
        <h1 className="sr-only">RecapMate access</h1>
        <a href="/" className="mx-auto flex w-fit items-center gap-2.5" aria-label="Back to RecapMate">
          <span className="admin-display flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-base font-bold text-white">R</span>
          <span className="admin-display text-lg font-semibold tracking-tight text-slate-950">RecapMate</span>
        </a>

        <form className="mt-8" onSubmit={submit} noValidate>
          <label htmlFor="admin-sign-in-key" className="sr-only">Access key</label>
          <div className="admin-access-control relative flex items-center rounded-2xl border border-slate-200 bg-white p-1.5">
            <input
              id="admin-sign-in-key"
              className="admin-access-field admin-mono min-w-0 flex-1 bg-transparent px-3 py-2.5 pr-10 text-sm text-slate-950"
              type={showKey ? "text" : "password"}
              value={key}
              onChange={(event) => setKey(event.target.value)}
              autoComplete="current-password"
              placeholder="Access key"
              spellCheck="false"
              aria-describedby={error ? "admin-sign-in-error" : undefined}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowKey((current) => !current)}
              className="absolute inset-y-1.5 right-[3.55rem] flex w-10 items-center justify-center rounded-xl text-slate-400 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label={showKey ? "Hide access key" : "Show access key"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              type="submit"
              className="flex h-10 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
              aria-label="Continue"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
          {error && (
            <p id="admin-sign-in-error" role="alert" className="mt-3 text-center text-sm font-medium text-red-700">
              {error}
            </p>
          )}
        </form>
      </section>
    </main>
  );
}

function KeyCell({ apiKey }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <code className="admin-mono min-w-0 truncate text-xs font-medium text-slate-800" title={revealed ? apiKey : undefined}>
        {revealed ? apiKey : maskKey(apiKey)}
      </code>
      <button type="button" onClick={() => setRevealed((value) => !value)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={revealed ? "Mask API key" : "Reveal API key"}>
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={() => copyText(apiKey, () => { setCopied(true); window.setTimeout(() => setCopied(false), 1400); })}
        className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        aria-label="Copy API key"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function QuotaMeter({ item }) {
  const percent = quotaPercent(item);
  const empty = item.remainingQuota <= 0;
  const low = !empty && percent <= 20;
  return (
    <div className="min-w-[150px]">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold text-slate-900">{number.format(item.remainingQuota)}</span>
        <span className="text-xs text-slate-500">of {number.format(item.totalQuota)}</span>
      </div>
      <div className="admin-quota-track mt-2" aria-label={`${Math.round(percent)} percent of quota remaining`}>
        <div className="admin-quota-fill" data-low={low} data-empty={empty} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function AdminConsole() {
  const [adminKey, setAdminKey] = useState(() => window.sessionStorage.getItem(SESSION_KEY) || "");
  const [authState, setAuthState] = useState(adminKey ? "checking" : "signedOut");
  const [authError, setAuthError] = useState("");
  const [pageData, setPageData] = useState(emptyPage);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState({ page: 1, pageSize: 20, query: "", status: "all", sort: "createdAt", direction: "desc" });
  const [refreshToken, setRefreshToken] = useState(0);
  const [notice, setNotice] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState("");
  const [createQuota, setCreateQuota] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState("");
  const [issuedKey, setIssuedKey] = useState("");
  const [topUpItem, setTopUpItem] = useState(null);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpBusy, setTopUpBusy] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const signOut = useCallback((message) => {
    window.sessionStorage.removeItem(SESSION_KEY);
    setAdminKey("");
    setAuthState("signedOut");
    setPageData(emptyPage);
    setAuthError(message || "");
  }, []);

  useEffect(() => {
    if (!adminKey || authState !== "checking") return;
    let active = true;
    validateAdminKey(adminKey)
      .then(() => { if (active) setAuthState("authenticated"); })
      .catch(() => { if (active) signOut("Your admin session could not be restored. Enter the key again."); });
    return () => { active = false; };
  }, [adminKey, authState, signOut]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    let active = true;
    setLoading(true);
    setLoadError("");
    listEntitlements(adminKey, filters)
      .then((data) => { if (active) setPageData(data); })
      .catch((error) => {
        if (!active) return;
        if (error instanceof AdminApiError && (error.status === 401 || error.status === 403)) {
          signOut("Your admin session expired. Enter the key again.");
          return;
        }
        setLoadError(error.message || "The quota ledger could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [adminKey, authState, filters, refreshToken, signOut]);

  const authenticate = (key) => {
    window.sessionStorage.setItem(SESSION_KEY, key);
    setAuthError("");
    setAdminKey(key);
    setAuthState("authenticated");
  };

  const refresh = () => setRefreshToken((value) => value + 1);
  const updateFilters = (patch) => setFilters((current) => ({ ...current, ...patch }));
  const stats = pageData.stats || emptyPage.stats;
  const usagePercent = stats.totalQuota ? Math.round((stats.consumedQuota / stats.totalQuota) * 100) : 0;

  const sortValue = `${filters.sort}:${filters.direction}`;
  const visibleRange = useMemo(() => {
    if (!pageData.totalItems) return "No matching keys";
    const start = (pageData.page - 1) * pageData.pageSize + 1;
    const end = Math.min(pageData.page * pageData.pageSize, pageData.totalItems);
    return `${number.format(start)}–${number.format(end)} of ${number.format(pageData.totalItems)}`;
  }, [pageData]);

  const submitSearch = (event) => {
    event.preventDefault();
    updateFilters({ query: searchDraft.trim(), page: 1 });
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    const quota = Number.parseInt(createQuota, 10);
    if (!Number.isInteger(quota) || quota <= 0) {
      setCreateError("Starting quota must be a whole number greater than zero.");
      return;
    }
    setCreateBusy(true);
    setCreateError("");
    try {
      const created = await createEntitlement(adminKey, { apiKey: createKey.trim(), quota });
      setCreateOpen(false);
      setCreateKey("");
      setCreateQuota("");
      setIssuedKey(created.apiKey);
      setNotice({ type: "success", message: "API key issued and added to the ledger." });
      refresh();
    } catch (error) {
      setCreateError(error.status === 409 ? "That API key already exists." : error.message || "The API key could not be created.");
    } finally {
      setCreateBusy(false);
    }
  };

  const submitTopUp = async (event) => {
    event.preventDefault();
    const amount = Number.parseInt(topUpAmount, 10);
    if (!Number.isInteger(amount) || amount <= 0) {
      setTopUpError("Credit amount must be a whole number greater than zero.");
      return;
    }
    setTopUpBusy(true);
    setTopUpError("");
    try {
      await addEntitlementQuota(adminKey, topUpItem.apiKey, amount);
      setTopUpItem(null);
      setTopUpAmount("");
      setNotice({ type: "success", message: `${number.format(amount)} credits added.` });
      refresh();
    } catch (error) {
      setTopUpError(error.message || "Credits could not be added.");
    } finally {
      setTopUpBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleteBusy(true);
    try {
      await deleteEntitlement(adminKey, deleteItem.apiKey);
      setDeleteItem(null);
      setNotice({ type: "success", message: "API key revoked." });
      refresh();
    } catch (error) {
      setNotice({ type: "error", message: error.message || "The API key could not be revoked." });
    } finally {
      setDeleteBusy(false);
    }
  };

  if (authState === "signedOut") {
    return <AdminSignIn onAuthenticated={authenticate} initialError={authError} />;
  }

  if (authState === "checking") {
    return (
      <main className="admin-shell flex min-h-screen items-center justify-center" aria-live="polite">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />
          <p className="admin-mono mt-4 text-xs uppercase tracking-[0.15em] text-slate-600">Restoring admin session</p>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-shell min-h-screen pb-16">
      <div className="mx-auto w-full max-w-[1500px] px-4 py-5 sm:px-6 lg:px-10">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-300 pb-5">
          <div className="flex items-center gap-3">
            <a href="/" className="admin-display flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950 text-lg font-bold text-white" aria-label="Back to RecapMate">R</a>
            <div>
              <p className="admin-display font-semibold text-slate-950">RecapMate control room</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                Production ledger
              </div>
            </div>
          </div>
          <button type="button" onClick={() => signOut()} className="admin-button admin-button-secondary">
            <LogOut className="h-4 w-4" /> End session
          </button>
        </header>

        <section className="pt-10">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="admin-mono text-xs font-medium uppercase tracking-[0.16em] text-blue-700">Quota operations</p>
              <h1 className="admin-display mt-3 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">The quota ledger</h1>
              <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">Find access, read its remaining runway, and make deliberate changes without leaving the ledger.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={refresh} className="admin-button admin-button-secondary" disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button type="button" onClick={() => { setCreateError(""); setCreateOpen(true); }} className="admin-button admin-button-primary">
                <Plus className="h-4 w-4" /> Issue API key
              </button>
            </div>
          </div>

          <div className="admin-ledger-panel relative mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="admin-signal-rail absolute inset-y-0 left-0 w-1" />
            <dl className="grid divide-y divide-slate-200 pl-1 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
              <div className="p-5 sm:p-6">
                <dt className="flex items-center gap-2 text-sm text-slate-500"><Database className="h-4 w-4" /> Keys in ledger</dt>
                <dd className="admin-display mt-2 text-3xl font-semibold text-slate-950">{number.format(stats.totalKeys)}</dd>
                <p className="mt-1 text-xs text-slate-500">{number.format(stats.activeKeys)} active · {number.format(stats.depletedKeys)} depleted</p>
              </div>
              <div className="p-5 sm:p-6">
                <dt className="flex items-center gap-2 text-sm text-slate-500"><Coins className="h-4 w-4" /> Credits remaining</dt>
                <dd className="admin-display mt-2 text-3xl font-semibold text-emerald-700">{number.format(stats.remainingQuota)}</dd>
                <p className="mt-1 text-xs text-slate-500">Across every active entitlement</p>
              </div>
              <div className="p-5 sm:p-6">
                <dt className="flex items-center gap-2 text-sm text-slate-500"><Activity className="h-4 w-4" /> Credits consumed</dt>
                <dd className="admin-display mt-2 text-3xl font-semibold text-slate-950">{number.format(stats.consumedQuota)}</dd>
                <p className="mt-1 text-xs text-slate-500">{usagePercent}% of all issued credits</p>
              </div>
              <div className="p-5 sm:p-6">
                <dt className="flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4" /> Ledger state</dt>
                <dd className="admin-display mt-2 text-3xl font-semibold text-blue-700">Live</dd>
                <p className="mt-1 text-xs text-slate-500">Admin session verified</p>
              </div>
            </dl>
          </div>
        </section>

        <section className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-labelledby="entitlements-heading">
          <div className="border-b border-slate-200 p-4 sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <h2 id="entitlements-heading" className="admin-display text-xl font-semibold text-slate-950">Entitlements</h2>
                <p className="mt-1 text-sm text-slate-500">{visibleRange}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                <form onSubmit={submitSearch} className="flex min-w-0 flex-1 gap-2 sm:min-w-[320px]">
                  <label htmlFor="admin-key-search" className="sr-only">Search API keys</label>
                  <div className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input id="admin-key-search" className="admin-field pl-9" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="Search by API key" />
                  </div>
                  <button type="submit" className="admin-button admin-button-secondary">Search</button>
                </form>
                <div>
                  <label htmlFor="admin-status-filter" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
                  <select id="admin-status-filter" className="admin-field min-w-[140px]" value={filters.status} onChange={(event) => updateFilters({ status: event.target.value, page: 1 })}>
                    <option value="all">All keys</option>
                    <option value="active">Active</option>
                    <option value="depleted">Depleted</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-sort" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Order</label>
                  <select
                    id="admin-sort"
                    className="admin-field min-w-[190px]"
                    value={sortValue}
                    onChange={(event) => { const [sort, direction] = event.target.value.split(":"); updateFilters({ sort, direction, page: 1 }); }}
                  >
                    <option value="createdAt:desc">Newest issued</option>
                    <option value="createdAt:asc">Oldest issued</option>
                    <option value="remainingQuota:asc">Lowest quota</option>
                    <option value="remainingQuota:desc">Highest quota</option>
                    <option value="updatedAt:desc">Recently changed</option>
                  </select>
                </div>
              </div>
            </div>
            {filters.query && (
              <button type="button" onClick={() => { setSearchDraft(""); updateFilters({ query: "", page: 1 }); }} className="mt-3 text-sm font-medium text-blue-700 hover:underline">
                Clear search for “{filters.query}”
              </button>
            )}
          </div>

          {loadError ? (
            <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-5" role="alert">
              <p className="font-semibold text-red-800">The ledger did not load.</p>
              <p className="mt-1 text-sm text-red-700">{loadError}</p>
              <button type="button" onClick={refresh} className="admin-button admin-button-secondary mt-4">Try again</button>
            </div>
          ) : loading && pageData.items.length === 0 ? (
            <div className="flex min-h-[300px] items-center justify-center" aria-live="polite">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-3 text-sm text-slate-600">Reading the ledger…</span>
            </div>
          ) : pageData.items.length === 0 ? (
            <div className="px-6 py-20 text-center">
              <KeyRound className="mx-auto h-9 w-9 text-slate-300" />
              <h3 className="admin-display mt-4 text-xl font-semibold text-slate-900">No keys match this view</h3>
              <p className="mt-2 text-sm text-slate-500">Change the filters or issue a new API key.</p>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="admin-table w-full border-collapse text-left">
                  <thead><tr><th className="px-5 py-4">API key</th><th className="px-5 py-4">Quota runway</th><th className="px-5 py-4">State</th><th className="px-5 py-4">Issued</th><th className="px-5 py-4 text-right">Actions</th></tr></thead>
                  <tbody>
                    {pageData.items.map((item) => (
                      <tr key={item.apiKey}>
                        <td className="max-w-[360px] px-5 py-5"><KeyCell apiKey={item.apiKey} /></td>
                        <td className="px-5 py-5"><QuotaMeter item={item} /></td>
                        <td className="px-5 py-5"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.remainingQuota > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{item.remainingQuota > 0 ? "Active" : "Depleted"}</span></td>
                        <td className="whitespace-nowrap px-5 py-5 text-sm text-slate-600">{formatDate(item.createdAt)}</td>
                        <td className="px-5 py-5"><div className="flex justify-end gap-2"><button type="button" onClick={() => { setTopUpError(""); setTopUpAmount(""); setTopUpItem(item); }} className="admin-button admin-button-secondary !min-h-9 !px-3 !py-1.5 text-xs"><Coins className="h-3.5 w-3.5" /> Add credits</button><button type="button" onClick={() => setDeleteItem(item)} className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500" aria-label={`Revoke key ${maskKey(item.apiKey)}`}><Trash2 className="h-4 w-4" /></button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="divide-y divide-slate-200 md:hidden">
                {pageData.items.map((item) => (
                  <article key={item.apiKey} className="p-5">
                    <KeyCell apiKey={item.apiKey} />
                    <div className="mt-5"><QuotaMeter item={item} /></div>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-500"><span>{item.remainingQuota > 0 ? "Active" : "Depleted"}</span><time>{formatDate(item.createdAt)}</time></div>
                    <div className="mt-5 grid grid-cols-[1fr_auto] gap-2"><button type="button" onClick={() => { setTopUpError(""); setTopUpAmount(""); setTopUpItem(item); }} className="admin-button admin-button-secondary"><Coins className="h-4 w-4" /> Add credits</button><button type="button" onClick={() => setDeleteItem(item)} className="admin-button border border-red-200 bg-red-50 !px-3 text-red-700" aria-label={`Revoke key ${maskKey(item.apiKey)}`}><Trash2 className="h-4 w-4" /></button></div>
                  </article>
                ))}
              </div>
            </>
          )}

          <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
            <span>Page {number.format(pageData.page)}{pageData.totalPages ? ` of ${number.format(pageData.totalPages)}` : ""}</span>
            <div className="flex gap-2">
              <button type="button" className="admin-button admin-button-secondary !min-h-9 !px-3" disabled={pageData.page <= 1 || loading} onClick={() => updateFilters({ page: filters.page - 1 })}><ChevronLeft className="h-4 w-4" /> Previous</button>
              <button type="button" className="admin-button admin-button-secondary !min-h-9 !px-3" disabled={!pageData.totalPages || pageData.page >= pageData.totalPages || loading} onClick={() => updateFilters({ page: filters.page + 1 })}>Next <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </section>
      </div>

      <AdminDialog open={createOpen} onOpenChange={setCreateOpen} title="Issue an API key" description="Leave the key field blank to generate a secure 64-character key automatically.">
        <form className="mt-6 space-y-5" onSubmit={submitCreate}>
          <div><label htmlFor="new-admin-api-key" className="block text-sm font-semibold text-slate-800">Custom key <span className="font-normal text-slate-500">(optional)</span></label><input id="new-admin-api-key" className="admin-field admin-mono mt-2" value={createKey} onChange={(event) => setCreateKey(event.target.value)} spellCheck="false" placeholder="Auto-generate when blank" /></div>
          <div><label htmlFor="new-admin-quota" className="block text-sm font-semibold text-slate-800">Starting credits</label><input id="new-admin-quota" className="admin-field mt-2" type="number" min="1" step="1" value={createQuota} onChange={(event) => setCreateQuota(event.target.value)} required /></div>
          {createError && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{createError}</p>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Dialog.Close asChild><button type="button" className="admin-button admin-button-secondary">Cancel</button></Dialog.Close><button type="submit" className="admin-button admin-button-primary" disabled={createBusy}>{createBusy && <Loader2 className="h-4 w-4 animate-spin" />} Issue key</button></div>
        </form>
      </AdminDialog>

      <AdminDialog open={Boolean(issuedKey)} onOpenChange={(open) => { if (!open) setIssuedKey(""); }} title="Key issued" description="Copy this key now and send it through the intended secure channel.">
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4"><code className="admin-mono block break-all text-sm text-emerald-950">{issuedKey}</code></div>
        <button type="button" onClick={() => copyText(issuedKey, () => setNotice({ type: "success", message: "API key copied." }))} className="admin-button admin-button-primary mt-4 w-full"><Copy className="h-4 w-4" /> Copy API key</button>
      </AdminDialog>

      <AdminDialog open={Boolean(topUpItem)} onOpenChange={(open) => { if (!open) setTopUpItem(null); }} title="Add credits" description={topUpItem ? `Increase the quota for ${maskKey(topUpItem.apiKey)}. Existing remaining credits are preserved.` : ""}>
        <form className="mt-6 space-y-5" onSubmit={submitTopUp}><div><label htmlFor="admin-top-up-amount" className="block text-sm font-semibold text-slate-800">Credits to add</label><input id="admin-top-up-amount" className="admin-field mt-2" type="number" min="1" step="1" value={topUpAmount} onChange={(event) => setTopUpAmount(event.target.value)} required autoFocus /></div>{topUpError && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{topUpError}</p>}<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Dialog.Close asChild><button type="button" className="admin-button admin-button-secondary">Cancel</button></Dialog.Close><button type="submit" className="admin-button admin-button-primary" disabled={topUpBusy}>{topUpBusy && <Loader2 className="h-4 w-4 animate-spin" />} Add credits</button></div></form>
      </AdminDialog>

      <AlertDialog.Root open={Boolean(deleteItem)} onOpenChange={(open) => { if (!open) setDeleteItem(null); }}>
        <AlertDialog.Portal><AlertDialog.Overlay className="fixed inset-0 z-[110] bg-slate-950/45 backdrop-blur-sm" /><AlertDialog.Content className="fixed left-1/2 top-1/2 z-[120] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-red-200 bg-white p-6 shadow-2xl"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-700"><Trash2 className="h-5 w-5" /></div><AlertDialog.Title className="admin-display mt-5 text-2xl font-semibold text-slate-950">Revoke this API key?</AlertDialog.Title><AlertDialog.Description className="mt-3 text-sm leading-6 text-slate-600">{deleteItem ? `${maskKey(deleteItem.apiKey)} will stop working immediately. Associated payment records for this key are also removed. This cannot be undone.` : ""}</AlertDialog.Description><div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><AlertDialog.Cancel asChild><button type="button" className="admin-button admin-button-secondary">Keep key</button></AlertDialog.Cancel><AlertDialog.Action asChild><button type="button" onClick={confirmDelete} className="admin-button admin-button-danger" disabled={deleteBusy}>{deleteBusy && <Loader2 className="h-4 w-4 animate-spin" />} Revoke key</button></AlertDialog.Action></div></AlertDialog.Content></AlertDialog.Portal>
      </AlertDialog.Root>

      <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[140] flex justify-center" aria-live="polite" aria-atomic="true">
        {notice && <div className={`pointer-events-auto flex max-w-md items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium shadow-lg ${notice.type === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}><span>{notice.message}</span><button type="button" onClick={() => setNotice(null)} className="ml-auto rounded p-1 hover:bg-black/5" aria-label="Dismiss notification"><X className="h-4 w-4" /></button></div>}
      </div>
    </main>
  );
}

export { maskKey };
export default AdminConsole;
